import { CartAddEvent } from '@theme/events';

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const content = this.querySelector('[data-recommendations-content]');
    if (!content || this.dataset.loaded === 'true') return;

    this.dataset.loaded = 'true';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${root}collections/workout-gear/products.json?limit=18`,
        { headers: { Accept: 'application/json' } }
      );

      if (!response.ok) throw new Error('Gym accessory collection request failed');

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      const cartProductIds = new Set(
        String(this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );

      const accessories = products
        .filter((product) => !cartProductIds.has(String(product.id)))
        .filter((product) => String(product.product_type || product.type || '').toLowerCase() === 'gym accessories')
        .filter((product) => Array.isArray(product.variants) && product.variants.some((variant) => variant.available));

      for (let i = accessories.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [accessories[i], accessories[j]] = [accessories[j], accessories[i]];
      }

      const selected = accessories.slice(0, 3);
      if (!selected.length) return;

      const details = await Promise.all(
        selected.map(async (product) => {
          try {
            const detailResponse = await fetch(
              `${root}products/${encodeURIComponent(product.handle)}.js`,
              { headers: { Accept: 'application/json' } }
            );
            if (!detailResponse.ok) return null;
            return await detailResponse.json();
          } catch (error) {
            console.warn('Armora Beast recommendation product data:', error);
            return null;
          }
        })
      );

      const usable = details.filter((product) => {
        if (!product || !Array.isArray(product.variants)) return false;
        return product.variants.some((variant) => variant.available);
      });

      if (!usable.length) return;

      content.innerHTML = `
        <h3 class="cart-drawer-recommendations__title">You May Also Like:</h3>
        <div class="cart-drawer-recommendations__list">
          ${usable.map((product) => this.renderProduct(product)).join('')}
        </div>
      `;
      content.hidden = false;

      content.querySelectorAll('[data-recommendation-add]').forEach((button) => {
        button.addEventListener('click', this.handleAdd);
      });
    } catch (error) {
      this.dataset.loaded = 'false';
      console.warn('Armora Beast cart recommendations:', error);
    }
  }

  renderProduct(product) {
    const variant = product.variants.find((item) => item.available) || product.variants[0];
    if (!variant) return '';

    const image = this.normalizeImageUrl(
      variant.featured_image?.src ||
      product.featured_image ||
      product.images?.[0] ||
      ''
    );

    const priceValue = variant.price ?? product.price;
    const compareValue = variant.compare_at_price ?? product.compare_at_price;
    const price = this.formatMoney(priceValue);
    const compareNumber = this.moneyToNumber(compareValue);
    const priceNumber = this.moneyToNumber(priceValue);
    const hasDiscount = compareNumber > priceNumber;
    const compare = hasDiscount ? this.formatMoney(compareValue) : '';
    const savingPercent = hasDiscount
      ? Math.round(((compareNumber - priceNumber) / compareNumber) * 100)
      : 0;

    const productUrl = product.url || `/products/${product.handle}`;
    const alt = product.featured_image_alt || product.title;

    return `
      <article class="cart-drawer-recommendation${hasDiscount ? ' cart-drawer-recommendation--sale' : ''}">
        <a class="cart-drawer-recommendation__media" href="${productUrl}" aria-label="${this.escape(product.title)}">
          ${hasDiscount ? `<span class="cart-drawer-recommendation__badge">${savingPercent}% OFF</span>` : ''}
          <img
            class="cart-drawer-recommendation__image"
            src="${this.escape(image)}"
            alt="${this.escape(alt)}"
            loading="eager"
            decoding="async"
            width="144"
            height="116"
          >
        </a>
        <div class="cart-drawer-recommendation__info">
          <a class="cart-drawer-recommendation__title" href="${productUrl}">${this.escape(product.title)}</a>
          <div class="cart-drawer-recommendation__price" aria-label="Price">
            <strong>${price}</strong>
            ${compare ? `<s>${compare}</s>` : ''}
          </div>
        </div>
        <button
          type="button"
          class="cart-drawer-recommendation__button"
          data-recommendation-add
          data-variant-id="${variant.id}"
          data-product-id="${product.id}"
        >${this.dataset.addLabel || '+ ADD'}</button>
      </article>
    `;
  }

  normalizeImageUrl(value) {
    if (!value) return '';
    return String(value).startsWith('//') ? `https:${value}` : String(value);
  }

  moneyToNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    const raw = String(value).trim();
    const number = Number(raw);
    if (!Number.isFinite(number)) return 0;
    return raw.includes('.') ? number : number / 100;
  }

  formatMoney(value) {
    const amount = this.moneyToNumber(value);

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `£${amount.toFixed(2)}`;
    }
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  handleAdd = async (event) => {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;

    if (!variantId || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = this.dataset.addingLabel || 'ADDING…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const sectionIds = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
        if (element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
      });

      if (sectionIds.size) formData.append('sections', [...sectionIds].join(','));
      formData.append('sections_url', window.location.pathname);

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add product');
      }

      document.dispatchEvent(new CartAddEvent({}, String(variantId), {
        source: 'cart-recommendation',
        itemCount: 1,
        productId,
        variantId: String(variantId),
        sections: data.sections || {},
      }));
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      console.error('Armora Beast recommendation add failed:', error);
    }
  };
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
